/**
 * VENDORED from tc-console/src/home/HomeSurface.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * Home — the opening view.
 *
 * WHAT IT IS. The one surface that is already open when you arrive, and the answer
 * to "what should I do here?": the hero, the tiles and the links its OWNER chose to
 * promote — the job a website's home page used to do. Guests see the ThinkingCap
 * site's content; a Learner View will see its client's, from that client's own
 * content repo. Same component either way.
 *
 * WHAT IT IS NOT. Not the scene panel: in the console, Home and the scene are
 * merged into tab zero (see HomeCanvas.tsx) — a projection replaces this start
 * page in place, and the tab itself never closes, so the console passes no
 * onClose. The GUEST view holds the same rule now: Home is persistent there
 * too (a cast displaces it, the Home icon brings it back), so no caller passes
 * onClose today — the prop and its X remain only as an option. And it is not a
 * markdown viewer — the server hands over
 * STRUCTURE (hero, sections, tiles, actions) and this lays it out in the
 * product's own type and spacing, so content pages look like the application
 * rather than like a document.
 *
 * NAVIGATION. It is a small website: a tile pointing at a page the corpus holds
 * opens IN PLACE with a Back stack, and only a link we do not hold leaves for the
 * browser. That is what `doc.routes` is for.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, Link, Menu,
  Paper, Stack,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HighlightAltIcon from '@mui/icons-material/HighlightAlt'
import MDXContent from '../mdx'
import { fileForHref, IMAGE_RE, type HomeAction, type HomeDoc, type HomeNavLink, type HomeSection, type HomeStat, type HomeTile } from './types'
import { renderNavMenuItems } from './navMenuItems'
import { composeWorkWithMe, PICK_ATTR, pickFromElement, picksInRect } from './workWithMe'
import Sparkline from './Sparkline'
import MaximizeToggle from '../shell/MaximizeToggle'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import StopIcon from '@mui/icons-material/Stop'
import PrintIcon from '@mui/icons-material/Print'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ShareIcon from '@mui/icons-material/Share'
import CheckIcon from '@mui/icons-material/Check'

/** Load the entry page (no argument) or one inner page. `windowDays` is the
 *  dashboard's cadence — ignored by the content variant, which has no numbers.
 *  Supplied by the caller so the same component serves the authenticated `/home`
 *  and the guest `/ask/home`. */
export type HomeLoader = (file?: string, windowDays?: number) => Promise<HomeDoc | null>

/** The one summary mp3 playing at a time. Starting a second stops the first —
 *  the same single-current rule useSpeech's read-aloud (`currentAudio`,
 *  ~64-73) follows — mirrored here rather than imported because this file
 *  vendors into lv-site-template and must stay self-contained. */
let currentSummaryAudio: { el: HTMLAudioElement; url: string } | null = null

/** Fired on window after a stop so EVERY mounted useSummaryAudio instance can
 *  drop its local playing flag — with two players on the page (summary +
 *  full article) starting one must visibly stop the other, not just mute it. */
const SUMMARY_AUDIO_STOPPED = 'tc:summary-audio-stopped'

function stopSummaryAudio(): void {
  if (currentSummaryAudio) {
    const url = currentSummaryAudio.url
    try { currentSummaryAudio.el.pause() } catch { /* ignore */ }
    currentSummaryAudio = null
    try { window.dispatchEvent(new CustomEvent(SUMMARY_AUDIO_STOPPED, { detail: url })) } catch { /* SSR/test */ }
  }
}

/** Play/stop for a page summary's or narration's mp3 (a plain public https
 *  URL). With no url the toggle is a no-op and playing stays false. */
function useSummaryAudio(url?: string): { playing: boolean; toggle: () => void } {
  const [playing, setPlaying] = useState(false)

  const toggle = useCallback(() => {
    if (!url) return
    if (playing && currentSummaryAudio?.url === url) {
      stopSummaryAudio()
      setPlaying(false)
      return
    }
    stopSummaryAudio() // single-current invariant: any other summary stops first
    const el = new Audio(url)
    currentSummaryAudio = { el, url }
    const reset = () => {
      if (currentSummaryAudio?.el === el) currentSummaryAudio = null
      setPlaying(false)
    }
    el.addEventListener('ended', reset)
    el.addEventListener('error', reset)
    void el.play().catch(reset)
    setPlaying(true)
  }, [url, playing])

  // An external stop (another player starting) mutes us — mirror it in state.
  useEffect(() => {
    const onStop = (e: Event) => {
      if ((e as CustomEvent<string>).detail === url) setPlaying(false)
    }
    window.addEventListener(SUMMARY_AUDIO_STOPPED, onStop)
    return () => window.removeEventListener(SUMMARY_AUDIO_STOPPED, onStop)
  }, [url])

  // Stop on unmount (and if the url ever changes under us).
  useEffect(() => () => {
    if (currentSummaryAudio?.url === url) stopSummaryAudio()
  }, [url])

  return { playing, toggle }
}

/** The generated summary under an opened page's title, presented as a proper
 *  abstract block (accent-ruled, tinted card, overline label, italic prose)
 *  so it reads as metadata about the article, not as the article's first
 *  paragraph. The only button inside is the summary's own play/stop — the
 *  full-article reader lives up in the title row (TitleActions). Renders
 *  nothing when the page has no summary. */
function SummaryRow({ doc }: { doc: HomeDoc }) {
  const { playing, toggle } = useSummaryAudio(doc.audioUrl)
  if (!doc.summary) return null
  const accent = doc.branding?.accent
  return (
    <Box
      sx={(t) => ({
        borderLeft: '3px solid',
        borderColor: accent ?? 'primary.main',
        borderRadius: 1,
        bgcolor: alpha(accent ?? t.palette.primary.main, t.palette.mode === 'dark' ? 0.14 : 0.07),
        px: 1.5,
        py: 1,
        mt: 0.5,
        mb: 1,
      })}
    >
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.2, letterSpacing: '0.08em', fontSize: '0.65rem' }}
          >
            Abstract
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', mt: 0.25 }}>
            {doc.summary}
          </Typography>
        </Box>
        {doc.audioUrl && (
          <Tooltip title={playing ? 'Stop summary' : 'Play summary'}>
            <IconButton
              size="small"
              aria-label={playing ? 'stop summary' : 'play summary'}
              onClick={toggle}
            >
              {playing ? <StopIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

/** Slug for the downloaded file's name — the title if we have one, else the
 *  source file's basename. */
function downloadName(doc: HomeDoc, title?: string): string {
  const raw = (title ?? doc.source.file.split('/').pop() ?? 'page').replace(/\.md$/, '')
  const slug = raw.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return slug || 'page'
}

/** The page-level actions pinned to a page's title line: read the whole
 *  article aloud (when the narration tier covered it), print the page,
 *  download its markdown, and share it. Used to live inside the abstract;
 *  Douglas (2026-09-24) wants them beside the title. Self-contained: print is
 *  window.print plus the PRINT_CSS below; download is a Blob of the raw
 *  markdown the server already handed over — no per-door URL knowledge, so
 *  this vendors to LV unchanged. Share (Douglas, 2026-10-02) copies a DEEP
 *  link to the page: the corpus's own public URL reversed out of doc.routes
 *  when the file has one, else the no-auth public-corpus URL /<door>/<path>
 *  (phoenix serves every corpus page there as HTML — the bare app origin is
 *  NOT a deep link). The door name rides in as a prop, so LV still carries
 *  no console knowledge. */
function TitleActions({ doc, title, door = 'guest' }: { doc: HomeDoc; title?: string; door?: 'guest' | 'rd' }) {
  const full = useSummaryAudio(doc.audioFullUrl)
  const [copied, setCopied] = useState(false)
  const download = () => {
    if (!doc.article) return
    const a = document.createElement('a')
    const url = URL.createObjectURL(new Blob([doc.article], { type: 'text/markdown' }))
    a.href = url
    a.download = `${downloadName(doc, title)}.md`
    a.click()
    URL.revokeObjectURL(url)
  }
  const share = async () => {
    const route = Object.entries(doc.routes).find(([, f]) => f === doc.source.file)?.[0]
    // Pages with no captured route (the Research-Development tree never had
    // public URLs) are still served no-auth from the public corpus tree at
    // /<door>/<path> — that is the deep link; the bare origin is not.
    const path = doc.source.file.replace(/^pages\//, '').replace(/\.md$/, '')
    const url = route ? `https://${route}` : `${window.location.origin}/${door}/${path}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard API refused (permissions, unfocused frame) — hand it over manually.
      window.prompt('Copy this link', url)
    }
  }
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" className="home-no-print" sx={{ flexShrink: 0 }}>
      {doc.audioFullUrl && (
        <Tooltip title={full.playing ? 'Stop full article' : 'Read the full article aloud'}>
          <IconButton
            size="small"
            aria-label={full.playing ? 'stop full article narration' : 'play full article narration'}
            onClick={full.toggle}
          >
            {full.playing ? <StopIcon fontSize="small" /> : <RecordVoiceOverIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Print this page">
        <IconButton size="small" aria-label="print this page" onClick={() => window.print()}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {doc.article && (
        <Tooltip title="Download the markdown">
          <IconButton size="small" aria-label="download the markdown" onClick={download}>
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={copied ? 'Link copied' : 'Share — copy a link to this page'}>
        <IconButton size="small" aria-label="copy a link to this page" onClick={share}>
          {copied ? <CheckIcon fontSize="small" /> : <ShareIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

/** Print just Home's content in a chat SPA: everything else on the page is
 *  visibility-hidden, the scroll container is pinned to the sheet, and the
 *  title-row buttons vanish (a printed button is dead weight). */
const PRINT_CSS = `@media print {
  body * { visibility: hidden; }
  .home-print-root, .home-print-root * { visibility: visible; }
  .home-print-root { position: absolute !important; left: 0; top: 0; width: 100%; max-height: none !important; overflow: visible !important; }
  .home-no-print, .home-no-print * { display: none !important; }
}`

function Tile({
  tile, accent, onFollow, onOpenSurface,
}: {
  tile: HomeTile
  accent?: string
  onFollow: (a: HomeAction) => void
  onOpenSurface?: (id: string, label?: string) => void
}) {
  const clickable = !!tile.action || !!tile.surface
  const open = () => {
    if (tile.surface) { onOpenSurface?.(tile.surface, tile.title); return }
    if (tile.action) onFollow(tile.action)
  }
  return (
    <Paper
      variant="outlined"
      onClick={clickable ? open : undefined}
      {...{ [PICK_ATTR]: 'tile' }}
      data-home-label={tile.title}
      data-home-detail={tile.body || undefined}
      data-home-target={tile.surface ?? tile.action?.href}
      sx={{
        borderRadius: '10px', overflow: 'hidden', height: '100%',
        display: 'flex', flexDirection: 'column',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color .15s, transform .15s',
        '&:hover': clickable ? { borderColor: accent ?? 'primary.main', transform: 'translateY(-2px)' } : undefined,
      }}
    >
      {tile.image && IMAGE_RE.test(tile.image) && (
        <Box
          component="img" src={tile.image} alt=""
          sx={{ width: '100%', height: 116, objectFit: 'cover', display: 'block' }}
        />
      )}
      <Stack spacing={0.75} sx={{ p: 1.75, flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>{tile.title}</Typography>
        {tile.meta && (
          <Typography variant="caption" color="text.disabled">{tile.meta}</Typography>
        )}
        {tile.body && <BodyMd text={tile.body} onFollow={onFollow} />}
        {(tile.surface || tile.action) && (
          <Typography
            variant="caption" sx={{ mt: 'auto', pt: 0.5, fontWeight: 700, color: accent ?? 'primary.main' }}
          >
            {tile.surface ? 'Open' : tile.action!.label} →
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

/**
 * Body text, rendered as real typography by the product's one MDX layer
 * (../mdx — MDXContent). The server sends bodies as RAW markdown — paragraph
 * breaks, bold/italic, lists, links, tables, code, figures — and the shared
 * renderer presents them; this adapter only maps Home's two concerns onto it:
 * the DENSITY (body1 callers are reading a document, body2 callers are inside
 * a card) and LINKS (every link rides onFollow: the corpus holds it, we
 * navigate in place; otherwise it opens in a new tab).
 */
function BodyMd({
  text, variant = 'body2', onFollow,
}: {
  text: string
  variant?: 'body1' | 'body2'
  onFollow: (a: HomeAction) => void
}) {
  return (
    <MDXContent
      text={text}
      density={variant === 'body1' ? 'article' : 'compact'}
      onLink={(href) => onFollow({ label: '', href })}
    />
  )
}

function Section({
  section, columns, accent, onFollow, onOpenSurface,
}: {
  section: HomeSection
  columns: number
  accent?: string
  onFollow: (a: HomeAction) => void
  onOpenSurface?: (id: string, label?: string) => void
}) {
  if (section.kind === 'callout') {
    const tile = section.tiles[0]
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: '10px', borderColor: accent ?? 'divider' }}
        {...{ [PICK_ATTR]: 'callout' }}
        data-home-label={section.title || tile?.title || 'Callout'}
        data-home-detail={section.body || tile?.body}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>{section.title || tile?.title}</Typography>
          {(section.body || tile?.body) && <BodyMd text={(section.body || tile?.body)!} onFollow={onFollow} />}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[...(tile?.action ? [tile.action] : []), ...section.actions].map((a) => (
              <Button
                key={a.href + a.label} size="small" variant="contained"
                onClick={() => onFollow(a)}
                sx={accent ? { bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(1.1)' } } : undefined}
              >
                {a.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Paper>
    )
  }

  // An inner content page's H3s are ARTICLE sections -- long prose with no link --
  // and squeezing those into a two-column card grid is unreadable. Cards are for
  // things you can go to; text is for things you read.
  const asArticle =
    section.kind === 'tiles' &&
    section.tiles.every((t) => !t.action) &&
    section.tiles.some((t) => t.body.length > 280)

  if (asArticle) {
    return (
      <Stack spacing={2}>
        {section.title && (
          <Typography variant="h6" fontWeight={800}>{section.title}</Typography>
        )}
        {section.body && <BodyMd text={section.body} onFollow={onFollow} />}
        {section.tiles.map((t) => (
          <Stack
            key={t.title} spacing={0.75}
            {...{ [PICK_ATTR]: 'passage' }}
            data-home-label={t.title}
            data-home-detail={t.body}
          >
            <Typography variant="subtitle1" fontWeight={700}>{t.title}</Typography>
            {t.meta && <Typography variant="caption" color="text.disabled">{t.meta}</Typography>}
            {t.image && IMAGE_RE.test(t.image) && (
              <Box
                component="img" src={t.image} alt=""
                sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: '10px' }}
              />
            )}
            <BodyMd text={t.body} onFollow={onFollow} />
          </Stack>
        ))}
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      {section.title && (
        <Typography variant="overline" color="text.secondary" letterSpacing={1}>
          {section.title}
        </Typography>
      )}
      {section.body && <BodyMd text={section.body} onFollow={onFollow} />}
      {section.kind === 'tiles' && (
        <Box
          sx={{
            display: 'grid', gap: 1.5,
            // One column below ~520px of PANEL width, not of viewport: Home lives in
            // a resizable pane, so the breakpoint has to be the container's.
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            '@container (max-width: 520px)': { gridTemplateColumns: '1fr' },
          }}
        >
          {section.tiles.map((t) => (
            <Tile key={t.title} tile={t} accent={accent} onFollow={onFollow} onOpenSurface={onOpenSurface} />
          ))}
        </Box>
      )}
      {section.actions.length > 0 && section.kind !== 'tiles' && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {section.actions.map((a) => (
            <Button key={a.href + a.label} size="small" onClick={() => onFollow(a)}>{a.label}</Button>
          ))}
        </Stack>
      )}
    </Stack>
  )
}

/** The dashboard's numbers. Clicking one goes to the surface where you would act
 *  on it -- a number you cannot chase is decoration. */
function Stats({
  stats, accent, onOpenSurface,
}: {
  stats: HomeStat[]
  accent?: string
  onOpenSurface?: (id: string, label?: string) => void
}) {
  return (
    <Box
      sx={{
        display: 'grid', gap: 1.5,
        // Two up, so four numbers make two rows and each card has room for its
        // shape. Four across leaves a sparkline too narrow to read.
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        '@container (max-width: 420px)': { gridTemplateColumns: '1fr' },
      }}
    >
      {stats.map((s) => {
        const clickable = !!s.surface && !!onOpenSurface
        return (
          <Paper
            key={s.label}
            variant="outlined"
            onClick={clickable ? () => onOpenSurface!(s.surface!) : undefined}
            {...{ [PICK_ATTR]: 'number' }}
            data-home-label={`${s.label} — ${s.value}`}
            data-home-detail={s.hint}
            data-home-target={s.surface}
            sx={{
              p: 1.75, borderRadius: '10px', overflow: 'hidden',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'border-color .15s',
              '&:hover': clickable ? { borderColor: accent ?? 'primary.main' } : undefined,
            }}
          >
            <Typography variant="h5" fontWeight={800} sx={{ color: accent ?? 'text.primary', lineHeight: 1.1 }}>
              {s.value}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{s.label}</Typography>
            {s.hint && (
              <Typography variant="caption" color="text.disabled" display="block">{s.hint}</Typography>
            )}
            {s.series && s.series.length > 1 && (
              // Full-bleed to the card's edges: the shape is the card's floor, not
              // a boxed figure inside it. The card clips it to its own radius.
              <Box sx={{ mt: 1.25, mx: -1.75, mb: -1.75 }}>
                <Sparkline values={s.series} accent={accent} label={s.label} />
              </Box>
            )}
          </Paper>
        )
      })}
    </Box>
  )
}

export default function HomeSurface({
  load,
  /** Present only where Home is its own closeable surface (the guest view).
   *  In the console Home is tab zero — the strip owns window chrome and the
   *  tab never closes, so no onClose (and no X) here. */
  onClose,
  /** Present in the signed-in shell, absent for a guest -- which is exactly why a
   *  guest's Home has no surface tiles to click. */
  onOpenSurface,
  /** "Work with me": hand the picked thing to the conversation. Absent = no button. */
  onWorkWithMe,
  /** Full screen — owned by the shell, which collapses the chat column. */
  maximized,
  onToggleMaximized,
  /** Chromeless mode (the console's tab zero): NO internal header bar — the
   *  tab strip already names the surface and carries its Work-with-me button,
   *  so a second bar is pure duplication. Back navigation and the site's nav
   *  menu move into the content flow instead. Absent (guest) = full chrome. */
  bare,
  /** Controlled pick-mode arming, for bare mode where the ARM button lives in
   *  the tab strip (SurfacePanel). Leave undefined to keep the state internal
   *  (the guest view's own header button). */
  picking: pickingProp,
  onPickingChange,
  /** GUEST header channels (absent everywhere else — the console's tab zero
   *  passes neither). onNavChange reports the effective site menu + current
   *  page whenever they change, so the guest header's Explore menu renders
   *  exactly what Home's nav row offers; navFollowRef is handed Home's own
   *  follow(), so an Explore pick navigates Home the way its own nav does. */
  onNavChange,
  navFollowRef,
  corpusDoor = 'guest',
}: {
  load: HomeLoader
  onClose?: () => void
  onOpenSurface?: (id: string, label?: string) => void
  onWorkWithMe?: (message: string) => void
  maximized?: boolean
  onToggleMaximized?: () => void
  bare?: boolean
  picking?: boolean
  onPickingChange?: (armed: boolean) => void
  onNavChange?: (items: HomeNavLink[], currentFile: string | undefined) => void
  navFollowRef?: { current: ((n: HomeNavLink) => void) | null }
  /** Which public corpus tree this Home serves ('guest' | 'rd') — the share
   *  button deep-links unrouted pages as /<door>/<path>. Only the guest view
   *  knows a second door exists; every other caller defaults to 'guest'. */
  corpusDoor?: 'guest' | 'rd'
}) {
  const [doc, setDoc] = useState<HomeDoc | null>(null)
  /** Pages visited, so Back walks home rather than closing. */
  const [trail, setTrail] = useState<HomeDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [empty, setEmpty] = useState(false)
  /** Armed for one pick, like the surface lasso: click a thing, ask about it, and
   *  the mode drops so the next click navigates again as normal. Controlled when
   *  the shell passes picking/onPickingChange (bare mode — the arm button is in
   *  the tab strip), internal otherwise (the guest header's own button). */
  const [pickingInner, setPickingInner] = useState(false)
  const picking = pickingProp ?? pickingInner
  function setPicking(v: boolean | ((p: boolean) => boolean)) {
    const next = typeof v === 'function' ? v(picking) : v
    if (pickingProp === undefined) setPickingInner(next)
    onPickingChange?.(next)
  }
  /** The dashboard's cadence. Server-chosen on first load, then the user's -- and a
   *  change refetches, because the numbers ARE the window. */
  const [windowDays, setWindowDays] = useState<number | undefined>(undefined)
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const d = await load()
        if (!alive) return
        if (!d) { setEmpty(true); return }
        setDoc(d)
        setWindowDays(d.windowDays)
      } catch (err) {
        if (alive) setError(String((err as Error).message ?? err))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [load])

  /** Switch cadence. Keeps the current numbers on screen while the new ones load --
   *  blanking the row on every toggle makes the dashboard feel broken. */
  const changeWindow = useCallback(async (days: number) => {
    setWindowDays(days)
    setSwitching(true)
    setError(null)
    try {
      const next = await load(undefined, days)
      if (next) setDoc(next)
    } catch (err) {
      setError(String((err as Error).message ?? err))
    } finally {
      setSwitching(false)
    }
  }, [load])

  const follow = useCallback(async (a: HomeAction) => {
    const file = (a as HomeNavLink).file ?? (doc ? fileForHref(doc, a.href) : undefined)
    if (!file) {
      // Not ours to render — hand it to the browser rather than fake a page.
      // Scheme-gated first: a javascript:/data: href would execute in THIS
      // origin even with noopener, so only real web/mail links are followed.
      if (/^(https?:|mailto:)/i.test(a.href)) window.open(a.href, '_blank', 'noopener,noreferrer')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const next = await load(file)
      if (next) {
        setTrail((t) => (doc ? [...t, doc] : t))
        setDoc(next)
      }
    } catch (err) {
      setError(String((err as Error).message ?? err))
    } finally {
      setLoading(false)
    }
  }, [doc, load])

  const back = useCallback(() => {
    setTrail((t) => {
      const prev = t[t.length - 1]
      if (prev) setDoc(prev)
      return t.slice(0, -1)
    })
  }, [])

  /* ── The lasso ──────────────────────────────────────────────────────────
   * Drag a box over anything in Home and the cap expands on what is inside it.
   *
   * The gesture is the Work Surface lasso's, but the mechanism is not: instead of
   * cropping a screenshot we ask which of Home's own tagged rectangles the box
   * touched (see workWithMe.ts). A drag shorter than the slop is a CLICK, which
   * picks the single thing under the pointer -- so both "this one" and "all of
   * that" work without two different modes.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const dragFrom = useRef<{ x: number; y: number } | null>(null)
  /** Both corners in CLIENT coords: that is what element rects are measured in, so
   *  the hit test needs no conversion. The marquee converts for display. */
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)

  const send = useCallback((picks: ReturnType<typeof picksInRect>) => {
    const message = composeWorkWithMe(picks)
    setPicking(false)
    setDrag(null)
    if (message) onWorkWithMe?.(message)
  }, [onWorkWithMe])

  /** Escape disarms. On the window, not on the overlay: an overlay that has to be
   *  focused first is an overlay you cannot cancel. */
  useEffect(() => {
    if (!picking) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      dragFrom.current = null
      setDrag(null)
      setPicking(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [picking])

  const onLassoDown = useCallback((e: ReactPointerEvent) => {
    dragFrom.current = { x: e.clientX, y: e.clientY }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setDrag({ x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY })
  }, [])

  const onLassoMove = useCallback((e: ReactPointerEvent) => {
    const from = dragFrom.current
    if (!from) return
    setDrag({ x0: from.x, y0: from.y, x1: e.clientX, y1: e.clientY })
  }, [])

  const onLassoUp = useCallback((e: ReactPointerEvent) => {
    const from = dragFrom.current
    dragFrom.current = null
    const root = scrollRef.current
    if (!from || !root) { setDrag(null); return }
    const dragged = Math.abs(e.clientX - from.x) > 4 || Math.abs(e.clientY - from.y) > 4
    if (!dragged) {
      // A click, not a drag. The overlay is on top, so ask the document what is
      // under the pointer rather than relying on the event's target.
      const el = document.elementFromPoint(e.clientX, e.clientY)
      const holder = el?.closest(`[${PICK_ATTR}]`)
      const one = holder ? pickFromElement(holder) : null
      send(one ? [one] : [])
      return
    }
    send(picksInRect(root, {
      left: Math.min(from.x, e.clientX),
      top: Math.min(from.y, e.clientY),
      right: Math.max(from.x, e.clientX),
      bottom: Math.max(from.y, e.clientY),
    }))
  }, [send])

  const accent = doc?.branding.accent
  const columns = doc?.branding.columns ?? 2
  const heroImage = doc?.branding.heroStyle === 'image'
    ? doc?.branding.heroImage ?? (doc?.hero.media && IMAGE_RE.test(doc.hero.media) ? doc.hero.media : undefined)
    : doc?.branding.heroImage

  /* The menu is site chrome, not page content: it persists at the top of
     every page, the way the old website's navbar did. Inner-page docs don't
     carry nav (the API only attaches it to the entry page), so we keep
     whichever doc in the trail HAS the menu. The current page's entry marks
     itself; clicking it is a no-op rather than a trail push. It renders only
     in bare mode's content flow (the signed-in console) — the guest Paper's
     copy was duplicate chrome and is gone; the doors' site navigation belongs
     to the global Explore menu, and doc.nav stays loaded for it. */
  /* Folder-derived menus: a nav entry with children is a dropdown, not a
     link — clicking it opens the folder's pages. One open menu at a time. */
  const [navMenu, setNavMenu] = useState<null | { el: HTMLElement; items: HomeNavLink[] }>(null)
  // Memoized: the guest header's Explore listener is notified on identity
  // change only — a fresh [] every render would ping-pong state forever.
  const menu = useMemo(
    () => (doc?.nav.length ? doc.nav : (trail.find((t) => t.nav.length)?.nav ?? [])),
    [doc, trail],
  )
  /* The guest header's Explore menu mirrors THIS menu — report it (and the
     current page, for the self-mark) whenever either moves. Absent listener
     (the console's tab zero) = nothing happens. */
  useEffect(() => {
    onNavChange?.(menu, doc?.source.file)
  }, [menu, doc?.source.file, onNavChange])
  /* ...and hand Home's own follow() down the ref, so an Explore pick takes
     exactly the path a click on Home's own nav row would. */
  useEffect(() => {
    if (!navFollowRef) return
    navFollowRef.current = (n) => { void follow(n) }
    return () => { navFollowRef.current = null }
  }, [navFollowRef, follow])
  const hasActiveLeaf = (items: HomeNavLink[]): boolean =>
    items.some((c) => (c.children?.length ? hasActiveLeaf(c.children) : !!c.file && c.file === doc?.source.file))

  const navRow = menu.length > 0 && doc && (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.75 }}>
      {menu.map((n) => {
        if (n.children?.length) {
          const active = hasActiveLeaf(n.children)
          return (
            <Link
              key={`menu-${n.label}`} component="button" variant="body2"
              underline={active ? 'always' : 'hover'}
              aria-haspopup="menu"
              onClick={(e) => setNavMenu({ el: e.currentTarget, items: n.children! })}
              sx={{
                color: accent ?? 'primary.main',
                fontWeight: active ? 800 : 600,
                cursor: 'pointer',
              }}
            >
              {n.label} ▾
            </Link>
          )
        }
        const active = !!n.file && n.file === doc.source.file
        return (
          <Link
            key={n.file ?? n.href} component="button" variant="body2"
            underline={active ? 'always' : 'hover'}
            onClick={() => { if (!active) void follow(n) }}
            sx={{
              color: accent ?? 'primary.main',
              fontWeight: active ? 800 : 600,
              cursor: active ? 'default' : 'pointer',
            }}
          >
            {n.label}
          </Link>
        )
      })}
      <Menu anchorEl={navMenu?.el ?? undefined} open={!!navMenu} onClose={() => setNavMenu(null)}>
        {navMenu
          ? renderNavMenuItems(navMenu.items, doc?.source.file, (n, active) => {
            setNavMenu(null)
            if (!active) void follow(n)
          })
          : <></>}
      </Menu>
    </Stack>
  )
  /* Back appears once you are inside a sub-page, with the current page's
     title beside it, dimmer. In bare mode it is the first content row rather
     than chrome — without it a sub-page would be a trap (tab zero has no X). */
  const backRow = trail.length > 0 && (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip title="Back">
        <IconButton size="small" onClick={back} aria-label="back">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography variant="subtitle2" sx={{ minWidth: 24, opacity: 0.75 }} noWrap>
        {doc?.hero.title || doc?.title}
      </Typography>
    </Stack>
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', containerType: 'inline-size' }}>
      {/* Print CSS for the title-row print button — scoped to Home's scroll
          root so the rest of the SPA (chat, tabs) never prints. */}
      <style>{PRINT_CSS}</style>
      {/* The full chrome bar is the GUEST view's — the guest has no tab strip,
          so this header is where its name, Back, Work-with-me and window
          controls live. In the console (bare) the tab strip IS the bar and
          this whole Paper would be a duplicate second header. */}
      {!bare && (
      <Paper elevation={0} square sx={{ borderBottom: 1, borderColor: 'divider', px: 1.5, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {/* The HOME glyph + name never leave the chrome — this header names
              the surface you are in, the same way the launcher entry does.
              Brand belongs in the content below (branding.logoUrl, rendered
              above the hero), not in the chrome. */}
          <HomeOutlinedIcon fontSize="small" sx={{ opacity: 0.8 }} />
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
            Home
          </Typography>
          {/* Back appears to the RIGHT of the home name once you are inside a
              sub-page; the current page's title follows it, dimmer. */}
          {backRow}
          <Typography sx={{ flex: 1, minWidth: 24 }} noWrap />
          {/* No "open the published page" escape hatch: Home IS the site now.
              Pages resolve inside the surface; the public web origin is being
              retired, so there is nothing useful to throw the visitor out to. */}
          {/* Named, not a glyph. Two bracket-ish icons side by side told nobody
              anything -- and this is the same labelled control the Work Surfaces
              tab strip has, so learning it once is learning it everywhere. */}
          {onWorkWithMe && (
            <Button
              size="small"
              variant={picking ? 'contained' : 'outlined'}
              startIcon={<HighlightAltIcon sx={{ fontSize: 16 }} />}
              onClick={() => setPicking((v) => !v)}
              aria-pressed={picking}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              Work with me
            </Button>
          )}
          {/* Window controls are a different KIND of thing from an action on the
              content, so they sit apart from it rather than a few pixels away. */}
          {onToggleMaximized && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
          )}
          {onToggleMaximized && (
            <MaximizeToggle maximized={!!maximized} onToggle={onToggleMaximized} />
          )}
          {/* The X belongs to the guest view, where Home is its own surface.
              In the console the tab strip owns window chrome and tab zero
              never closes — no onClose is passed, so no X renders. */}
          {onClose && (
            <Tooltip title="Close Home">
              <IconButton size="small" onClick={onClose} aria-label="close home">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>
      )}

      {picking && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5, textAlign: 'center' }}>
          Drag a box over anything here — or click one thing — and I'll expand on it.
        </Typography>
      )}

      <Box
        ref={scrollRef}
        className="home-print-root"
        sx={{
          flex: 1, overflowY: 'auto', p: 2, position: 'relative',
          // Armed: every pickable thing shows its edges, so it is obvious what a
          // box will catch before you drag it.
          ...(picking
            ? {
              [`& [${PICK_ATTR}]`]: {
                outline: '1px dashed',
                outlineColor: accent ?? 'primary.main',
                outlineOffset: 2,
                borderRadius: 1,
              },
            }
            : {}),
        }}
      >
        {/* The lasso catcher. Present only while armed, above the content so a drag
            is never stolen by a tile's own click, and cancelled on Escape. */}
        {picking && (
          <Box
            onPointerDown={onLassoDown}
            onPointerMove={onLassoMove}
            onPointerUp={onLassoUp}
            sx={{
              // ABSOLUTE, not fixed: the catcher covers Home's content and nothing
              // else, so the header -- including the button that disarms it -- and
              // the chat beside it stay clickable while armed.
              position: 'absolute', inset: 0, minHeight: '100%', zIndex: 20,
              cursor: 'crosshair',
              // No background: you have to be able to see what you are lassoing.
              touchAction: 'none',
            }}
          />
        )}
        {(() => {
          if (!drag) return null
          const w = Math.abs(drag.x1 - drag.x0)
          const h = Math.abs(drag.y1 - drag.y0)
          if (w < 4 || h < 4) return null
          const root = scrollRef.current
          if (!root) return null
          // Client -> container content coords, scroll included, because the marquee
          // is a child of the scrolling box.
          const r = root.getBoundingClientRect()
          const left = Math.min(drag.x0, drag.x1) - r.left + root.scrollLeft
          const top = Math.min(drag.y0, drag.y1) - r.top + root.scrollTop
          return (
            <Box
              sx={(t) => ({
                position: 'absolute', zIndex: 21, pointerEvents: 'none',
                left, top, width: w, height: h,
                border: '1.5px solid', borderColor: accent ?? t.palette.primary.main,
                bgcolor: alpha(accent ?? t.palette.primary.main, 0.12),
                borderRadius: 1,
              })}
            />
          )
        })()}

        {loading && !doc ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
        ) : empty ? (
          <Stack spacing={1} sx={{ mt: 2, textAlign: 'center', px: 2 }}>
            <Typography variant="body2" color="text.secondary">Nothing to show here yet.</Typography>
            <Typography variant="caption" color="text.disabled">
              Home is built from a content repo — once one is registered, it appears here.
            </Typography>
          </Stack>
        ) : !doc ? null : (
          <Stack spacing={3} sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* Bare mode: Back and the site menu live in the content flow —
                the tab strip above is the only bar. */}
            {bare && (trail.length > 0 || menu.length > 0) && (
              <Stack spacing={0.75}>
                {backRow}
                {navRow}
              </Stack>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {heroImage && (
              <Box
                component="img" src={heroImage} alt=""
                sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '10px' }}
              />
            )}

            {doc.branding.logoUrl && trail.length === 0 && (
              <Box
                component="img" src={doc.branding.logoUrl} alt=""
                sx={{ height: 34, maxWidth: 220, objectFit: 'contain', objectPosition: 'left' }}
              />
            )}

            {/* Article-shaped pages (server-set doc.article): one faithful
                document, no hero/cards chopping — a white paper reads like one.
                The page's own H1 is lifted into a real title row carrying the
                page actions (read-aloud, print, download); the abstract below
                keeps only its own play button. A cover (server-hoisted from the
                page's lone image, or frontmatter) banners the very top. */}
            {doc.article && (() => {
              const h1 = /^#[ \t]+(.+?)[ \t]*#*[ \t]*$/m.exec(doc.article)
              const title = h1 && h1.index < 100 ? h1[1] : null
              const body = title ? doc.article.slice(0, h1!.index) + doc.article.slice(h1!.index + h1![0].length) : doc.article
              return (
                <Stack
                  spacing={0.5}
                  {...{ [PICK_ATTR]: 'page' }}
                  data-home-label={title || doc.hero.title || doc.title}
                  data-home-detail={doc.article.slice(0, 2000)}
                >
                  {doc.cover && (
                    <Box
                      component="img" src={doc.cover} alt=""
                      sx={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 3, mb: 1 }}
                    />
                  )}
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    {title && (
                      <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: -0.5, flex: 1 }}>
                        {title}
                      </Typography>
                    )}
                    {!title && <Box sx={{ flex: 1 }} />}
                    <TitleActions doc={doc} title={title ?? undefined} door={corpusDoor} />
                  </Stack>
                  {doc.summary && <SummaryRow doc={doc} />}
                  <BodyMd text={body} variant="body1" onFollow={follow} />
                </Stack>
              )
            })()}

            {!doc.article && (<>
            <Stack
              spacing={1.5}
              {...{ [PICK_ATTR]: 'page' }}
              data-home-label={doc.hero.title || doc.title}
              data-home-detail={doc.hero.body}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: -0.5, flex: 1 }}>
                  {doc.hero.title || doc.title}
                </Typography>
                <TitleActions doc={doc} title={doc.hero.title || doc.title} door={corpusDoor} />
              </Stack>
              {/* Publish date under the title (source.lastmod — the parser's
                  date ?? lastmod frontmatter pick). */}
              {doc.source.lastmod && (
                <Typography variant="caption" color="text.secondary">
                  Published {doc.source.lastmod}
                </Typography>
              )}
              {doc.summary && <SummaryRow doc={doc} />}
              {doc.hero.body && (
                <BodyMd text={doc.hero.body} variant="body1" onFollow={follow} />
              )}
              {doc.hero.actions.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                  {doc.hero.actions.map((a, i) => (
                    <Button
                      key={a.href + a.label}
                      size="small"
                      variant={i === 0 ? 'contained' : 'outlined'}
                      onClick={() => void follow(a)}
                      sx={accent && i === 0
                        ? { bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(1.1)' } }
                        : accent ? { color: accent, borderColor: accent } : undefined}
                    >
                      {a.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>

            {doc.stats.length > 0 && (
              <Stack spacing={1}>
                {(doc.windows?.length ?? 0) > 1 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={windowDays ?? doc.windowDays ?? null}
                      onChange={(_e, v) => { if (typeof v === 'number') void changeWindow(v) }}
                      aria-label="how far back the numbers go"
                    >
                      {doc.windows!.map((w) => (
                        <ToggleButton key={w} value={w} sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>
                          {w} days
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {switching && <CircularProgress size={14} />}
                  </Stack>
                )}
                <Stats stats={doc.stats} accent={accent} onOpenSurface={onOpenSurface} />
                {doc.statsNote && (
                  <Typography variant="caption" color="text.disabled">{doc.statsNote}</Typography>
                )}
              </Stack>
            )}

            {doc.sections.map((s) => (
              <Section
                key={s.id} section={s} columns={columns} accent={accent}
                onFollow={(a) => void follow(a)} onOpenSurface={onOpenSurface}
              />
            ))}
            </>)}

            {doc.branding.showFooter !== false && doc.footer.length > 0 && (
              <>
                <Divider />
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pb: 1 }}>
                  {doc.footer.map((f) => (
                    <Link
                      key={f.href} component="button" variant="caption" underline="hover"
                      color="text.secondary" onClick={() => void follow(f)}
                    >
                      {f.label}
                    </Link>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
